"use client";

import { useState } from "react";
import { trackEvent } from "@/lib/analytics";

const INPUT_CLASS =
  "w-full px-4 py-3 border-[1.5px] border-border rounded-[10px] text-sm font-sans text-charcoal bg-warm-bg transition-all outline-none focus:border-gold focus:shadow-[0_0_0_3px_rgba(196,151,90,0.1)] focus:bg-white placeholder:text-[#B0A99F] min-h-[44px]";

export function ReferForm() {
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [email, setEmail] = useState("");
  const [type, setType] = useState<"university_society" | "brand_ambassador">(
    "university_society"
  );
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/partnership-enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          organisation: org,
          email,
          type,
          message,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error ?? "Something went wrong. Please try again.");
      } else {
        setSubmitted(true);
        trackEvent("partnership_application_submitted");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }

    setSubmitting(false);
  }

  if (submitted) {
    return (
      <div className="bg-success/10 border border-success/20 rounded-[10px] px-6 py-8 text-center">
        <svg
          className="w-10 h-10 text-success mx-auto mb-3"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <p className="text-navy text-[15px] font-semibold mb-1">
          Thanks! We&apos;ll be in touch within 24 hours.
        </p>
        <p className="text-text-muted text-[13px]">
          We&apos;ll send your tracking link and next steps to{" "}
          <span className="font-medium text-navy">{email}</span>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2 block">
          Your name *
        </label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={INPUT_CLASS}
          placeholder="Your full name"
        />
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2 block">
          Society or brand name *
        </label>
        <input
          type="text"
          required
          value={org}
          onChange={(e) => setOrg(e.target.value)}
          className={INPUT_CLASS}
          placeholder="e.g. Bristol Snowsports, The North Face"
        />
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2 block">
          Email *
        </label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={INPUT_CLASS}
          placeholder="you@example.com"
        />
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-3 block">
          Type *
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
            <input
              type="radio"
              name="type"
              value="university_society"
              checked={type === "university_society"}
              onChange={() => setType("university_society")}
              className="w-4 h-4 accent-gold"
            />
            <span className="text-sm text-text-body">University Society</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
            <input
              type="radio"
              name="type"
              value="brand_ambassador"
              checked={type === "brand_ambassador"}
              onChange={() => setType("brand_ambassador")}
              className="w-4 h-4 accent-gold"
            />
            <span className="text-sm text-text-body">Brand Ambassador</span>
          </label>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2 block">
          Message
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          className={`${INPUT_CLASS} resize-y`}
          placeholder="Tell us a bit about your society or brand"
        />
      </div>

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-3 text-sm font-medium">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-gold text-navy px-6 py-3.5 rounded-sm text-[15px] font-semibold border-none cursor-pointer font-sans transition-all hover:bg-gold-light min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed mt-2"
      >
        {submitting ? "Sending..." : "Send application"}
      </button>
    </form>
  );
}
